import type { TaxYear } from '../../types/tax';
import type { TaxYearStrategy } from './TaxYearStrategy';
import { 
  TaxYear2023_24Strategy, 
  TaxYear2024_25Strategy, 
  TaxYear2025_26Strategy,
  TaxYear2026_27Strategy
} from './strategies';

export class TaxDataManager {
  private static instance: TaxDataManager;
  private currentStrategy: TaxYearStrategy;
  private strategies: Map<TaxYear, TaxYearStrategy>;
  private subscribers: ((taxYear: TaxYear) => void)[] = [];

  private constructor() {
    // Initialize strategies
    this.strategies = new Map([
      ['2023-24', new TaxYear2023_24Strategy()],
      ['2024-25', new TaxYear2024_25Strategy()],
      ['2025-26', new TaxYear2025_26Strategy()],
      ['2026-27', new TaxYear2026_27Strategy()]
    ]);
    
    // Set default strategy to current tax year
    this.currentStrategy = this.strategies.get('2026-27')!;
  }

  public static getInstance(): TaxDataManager {
    if (!TaxDataManager.instance) {
      TaxDataManager.instance = new TaxDataManager();
    }
    return TaxDataManager.instance;
  }

  // Strategy management
  public setTaxYear(taxYear: TaxYear): void {
    const strategy = this.strategies.get(taxYear);
    if (!strategy) {
      throw new Error(`Tax year strategy not found for: ${taxYear}`);
    }
    
    const previousYear = this.currentStrategy.getYear();
    this.currentStrategy = strategy;
    
    // Notify subscribers if the year actually changed
    if (previousYear !== taxYear) {
      this.notifySubscribers(taxYear);
    }
  }

  public getCurrentTaxYear(): TaxYear {
    return this.currentStrategy.getYear();
  }

  public getCurrentStrategy(): TaxYearStrategy {
    return this.currentStrategy;
  }

  public getStrategy(taxYear: TaxYear): TaxYearStrategy {
    const strategy = this.strategies.get(taxYear);
    if (!strategy) {
      throw new Error(`Tax year strategy not found for: ${taxYear}`);
    }
    return strategy;
  }

  public getAvailableTaxYears(): TaxYear[] {
    return Array.from(this.strategies.keys());
  }

  // Convenience methods that delegate to current strategy
  public getTaxBrackets(): import('../../types/tax').TaxBracket[] {
    return this.currentStrategy.getTaxBrackets();
  }

  public getTaxRebates(): import('../../types/tax').TaxRebate {
    return this.currentStrategy.getTaxRebates();
  }

  public getTaxThresholds(): import('../../types/tax').TaxThresholds {
    return this.currentStrategy.getTaxThresholds();
  }

  public getMedicalAidCredits(): { mainMember: number; firstDependent: number; additionalDependent: number; } {
    return this.currentStrategy.getMedicalAidCredits();
  }

  public getTotalRebate(ageCategory: 'under65' | 'age65to74' | 'age75plus'): number {
    // Cast to base class to access helper methods
    return (this.currentStrategy as import('./TaxYearStrategy').BaseTaxYearStrategy).getTotalRebate(ageCategory);
  }

  public getMedicalAidCredit(mainMember: boolean, dependents: number): number {
    // Cast to base class to access helper methods  
    return (this.currentStrategy as import('./TaxYearStrategy').BaseTaxYearStrategy).getMedicalAidCredit(mainMember, dependents);
  }

  public getDisplayName(): string {
    return this.currentStrategy.getDisplayName();
  }

  public getStartDate(): Date {
    return this.currentStrategy.getStartDate();
  }

  public getEndDate(): Date {
    return this.currentStrategy.getEndDate();
  }

  // Tax year comparison utilities
  public compareTaxYears(fromYear: TaxYear, toYear: TaxYear): {
    brackets: boolean;
    rebates: boolean;
    thresholds: boolean;
    medicalAidCredits: boolean;
    summary: string;
  } {
    const fromStrategy = this.getStrategy(fromYear);
    const toStrategy = this.getStrategy(toYear);
    
    const bracketsChanged = JSON.stringify(fromStrategy.getTaxBrackets()) !== JSON.stringify(toStrategy.getTaxBrackets());
    const rebatesChanged = JSON.stringify(fromStrategy.getTaxRebates()) !== JSON.stringify(toStrategy.getTaxRebates());
    const thresholdsChanged = JSON.stringify(fromStrategy.getTaxThresholds()) !== JSON.stringify(toStrategy.getTaxThresholds());
    const medicalAidChanged = JSON.stringify(fromStrategy.getMedicalAidCredits()) !== JSON.stringify(toStrategy.getMedicalAidCredits());
    
    let summary = '';
    if (fromYear === '2023-24' && toYear === '2024-25') {
      summary = 'Medical aid tax credits increased significantly in 2024-25.';
    } else if (fromYear === '2024-25' && toYear === '2025-26') {
      summary = 'No changes between 2024-25 and 2025-26 tax years.';
    } else if (fromYear === '2025-26' && toYear === '2026-27') {
      summary = 'New tax brackets, increased rebates, thresholds, and medical aid credits for 2026-27.';
    } else if (!bracketsChanged && !rebatesChanged && !thresholdsChanged && !medicalAidChanged) {
      summary = 'No changes detected between tax years.';
    } else {
      const changes = [];
      if (bracketsChanged) changes.push('tax brackets');
      if (rebatesChanged) changes.push('tax rebates');
      if (thresholdsChanged) changes.push('tax thresholds');
      if (medicalAidChanged) changes.push('medical aid credits');
      summary = `Changes in: ${changes.join(', ')}.`;
    }
    
    return {
      brackets: bracketsChanged,
      rebates: rebatesChanged,
      thresholds: thresholdsChanged,
      medicalAidCredits: medicalAidChanged,
      summary
    };
  }

  // Observer pattern for tax year changes
  public subscribe(callback: (taxYear: TaxYear) => void): void {
    this.subscribers.push(callback);
  }

  public unsubscribe(callback: (taxYear: TaxYear) => void): void {
    const index = this.subscribers.indexOf(callback);
    if (index > -1) {
      this.subscribers.splice(index, 1);
    }
  }

  private notifySubscribers(taxYear: TaxYear): void {
    this.subscribers.forEach(callback => callback(taxYear));
  }

  // Auto-detect current tax year based on date
  public autoDetectTaxYear(): TaxYear {
    const now = new Date();
    
    for (const strategy of this.strategies.values()) {
      if (now >= strategy.getStartDate() && now <= strategy.getEndDate()) {
        return strategy.getYear();
      }
    }
    
    // Default to 2026-27 if detection fails
    return '2026-27';
  }

  // Initialize to current tax year
  public initializeToCurrentYear(): void {
    const currentYear = this.autoDetectTaxYear();
    this.setTaxYear(currentYear);
  }
}